/**
 * The `fixture` source: a whole tenant made of invented records, so the console runs with no API.
 *
 * It is built from the demo scenario in `src/mock/c3` (incidents, campaigns, entities, source
 * catalogue), turned into the ring documents `renglo-api` returns. Every screen reads the same
 * records, and the Overview counters are counted from them with the same code the real tenant uses,
 * so the band never says 46 incidents while the feed shows 12 (item 1, decisions/005).
 *
 * Nothing here is real. Times are relative to `now`, so the data always looks recent. The business
 * services, the blast radius and the entity-risk drivers are made up to give the graph and the risk
 * lists something to show; they are not derived from any measurement.
 */
import {
  C3_CAMPAIGNS,
  C3_ENTITIES,
  CAMP_OF,
  DEMO_SOURCE_CATALOG,
  makeC3Incidents,
  type C3Entity,
  type C3MockIncident,
} from '../../mock/c3';
import { DEFAULT_FUNNEL_WINDOW, type FunnelWindow } from '../../lib/funnel';
import { CROWN_JEWEL_TAG } from '../../lib/crownJewel';
import { computeFunnelCounters } from '../selectors/funnelCounters';
import type { C3DataSource } from '../ports';
import type {
  BlastRadiusScoreDocument,
  BusinessElementDocument,
  EntityRiskScoreDocument,
  InfrastructureElementDocument,
  RemediationItem,
  SecurityEventItem,
  SourceItem,
  ThreatElementDocument,
  ThreatEventCandidateItem,
  ThreatEventItem,
} from '../domain/types';

const MINUTE = 60 * 1000;

/** The assets of the invented tenant that carry the Crown Jewels tag (client notes 11/2). */
const CROWN_JEWEL_ASSETS = new Set(['prod-account', 'data-lake', 'Payments platform']);
/** Where each asset of the invented tenant sits (the Position tag of the notes 11/2); an asset not listed has none. */
const POSITION_OF_ASSET: Record<string, string> = {
  'j.chen': 'internal',
  'm.ortiz': 'internal',
  'eng-laptop-114': 'internal',
  'fin-laptop-registry': 'internal',
  'hr-laptop-032': 'internal',
  'prod-account': 'production',
  'data-lake': 'production',
  'dba-role': 'production',
  'build-server-07': 'non-production',
  'backup-vault': 'internal',
  'ci-runner-legacy': 'development',
  'k8s-node-4': 'production',
  'rds-reporting': 'production',
  'audit-account': 'internal',
  'prod-vpc': 'production',
  'data-acct': 'production',
  'it-admin-2': 'internal',
  'file-share-02': 'internal',
  'vendor-acct-11': 'public',
  'exec-cohort': 'internal',
  'all-tenants': 'public',
  'Payments platform': 'public',
  'Customer data platform': 'production',
  'Engineering delivery': 'internal',
};
const assetTags = (name: string) => {
  const tags = [...(CROWN_JEWEL_ASSETS.has(name) ? [CROWN_JEWEL_TAG] : []), ...(POSITION_OF_ASSET[name] ? [POSITION_OF_ASSET[name]] : [])];
  return tags.length ? { tags } : {};
};

/**
 * Three plans caught earlier in their life than the scenario's states reach, so the Demo shows every state the Threat Feed has
 * (client notes 10/26, definition 4). The two that are not decided by anyone are automatic ones, so My Queue does not count them.
 */
const EARLIER_IN_LIFE: Record<string, 'unassigned' | 'assigned' | 'escalated'> = {
  'INC-3953': 'assigned',
  'INC-3956': 'escalated',
  'INC-3957': 'unassigned',
};

export interface FixtureOptions {
  /** The moment the records are dated from. Defaults to the time the source is created. */
  now?: number;
}

export interface FixtureRecords {
  threatEvents: ThreatEventItem[];
  threatCandidates: ThreatEventCandidateItem[];
  threatElements: ThreatElementDocument[];
  infrastructureElements: InfrastructureElementDocument[];
  businessElements: BusinessElementDocument[];
  securityEvents: SecurityEventItem[];
  remediations: RemediationItem[];
  blastRadiusScores: BlastRadiusScoreDocument[];
  entityRiskScores: EntityRiskScoreDocument[];
  sources: SourceItem[];
}

/** "AWS CloudTrail" becomes "aws_cloudtrail", the way providers are written in the rings. */
function providerSlug(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** An incident can list several tools ("Okta → AWS → CrowdStrike"); the first one raised it. */
function sourceLabels(source: string): string[] {
  return source
    .split('→')
    .map((part) => part.trim())
    .filter(Boolean);
}

function link(value: string, label: string) {
  return { value, label: [label] };
}

function bandOf(score: number): string {
  if (score >= 90) return 'critical';
  if (score >= 75) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}

/** "+31" and "−4" (with the typographic minus) as numbers. */
function trendValue(trend: string): number {
  const n = Number(trend.replace('−', '-').replace('+', ''));
  return Number.isFinite(n) ? n : 0;
}

function domainOfEntity(entity: C3Entity): { domain: string; type: string } {
  const [domain, type = ''] = entity.type.split('·').map((part) => part.trim().toLowerCase());
  return { domain, type };
}

/** The domain of an asset that is not one of the entities, guessed from its name. */
function domainOfAsset(asset: string): string {
  const name = asset.toLowerCase();
  if (/(laptop|server|node|endpoint|cohort|host)/.test(name)) return 'endpoint';
  if (/(user|acct|account|iam|tenant|\.)/.test(name)) return 'identity';
  return 'cloud';
}

const BUSINESS_SERVICES: { id: string; name: string; type: string; description: string; runsOn: string[] }[] = [
  {
    id: 'BIZ-payments',
    name: 'Payments platform',
    type: 'business_service',
    description: 'Card payments, hosted in the production account',
    runsOn: ['prod-account', 'dba-role'],
  },
  {
    id: 'BIZ-customer-data',
    name: 'Customer data platform',
    type: 'data_asset',
    description: 'Customer exports and analytics, stored in the data lake',
    runsOn: ['data-lake'],
  },
  {
    id: 'BIZ-delivery',
    name: 'Engineering delivery',
    type: 'business_service',
    description: 'Builds and releases, run from the engineering fleet',
    runsOn: ['eng-laptop-114', 'build-server-07'],
  },
];

/**
 * The second half of CAMP-02 in the scenario ("Cross-plane privilege escalation" lists INC-4025 and
 * INC-4022), which the mock never defines. Without it the campaign would have one incident and the
 * feed would show it as a lone one.
 */
const SECOND_CAMPAIGN_MEMBER: C3MockIncident = {
  id: 'INC-4022',
  sev: 'critical',
  title: 'IAM policy self-elevation to administrator',
  source: 'AWS CloudTrail → Okta',
  asset: 'prod-account',
  state: 'progress',
  xdom: true,
  detail: {
    signals: [
      ['08:09:40', 'AWS CloudTrail', 'AttachUserPolicy with AdministratorAccess by the same principal that created it.'],
      ['08:10:02', 'Okta', 'Admin role assigned outside the change window.'],
    ],
    reasoning:
      'The principal granted itself administrator rights in the production account and an Okta admin role within a minute, which no change request covers.',
    confidence: 0.93,
    attack: ['T1098 Account Manipulation', 'T1078 Valid Accounts'],
    chain: [['Detach the policy and revoke the admin role', 'progress']],
    audit: [
      ['08:10:05', 'ai', 'Classified (conf 0.93), joined to CAMP-02'],
      ['08:10:09', 'ai', 'Containment started'],
    ],
  },
};

export function buildFixtureRecords({ now = Date.now() }: FixtureOptions = {}): FixtureRecords {
  const iso = (minutesAgo: number) => new Date(now - minutesAgo * MINUTE).toISOString();
  const incidents = [...makeC3Incidents(), SECOND_CAMPAIGN_MEMBER];

  // --- Infrastructure: the entities, plus every asset that is not one of them.
  const infraByName = new Map<string, InfrastructureElementDocument>();
  const infraId = (name: string) => `INF-${providerSlug(name)}`;
  for (const entity of C3_ENTITIES) {
    const { domain, type } = domainOfEntity(entity);
    infraByName.set(entity.n, {
      _id: infraId(entity.n),
      name: entity.n,
      provider: domain,
      provider_type: type,
      universal_type: domain,
      universal_domain: domain,
      external_id: entity.n,
      ...assetTags(entity.n),
      links: [],
    });
  }
  const entityNames = C3_ENTITIES.map((entity) => entity.n);
  const infraRefsOf = (incident: C3MockIncident): string[] => {
    const named = C3_ENTITIES.filter(
      (entity) => entity.rel.includes(incident.id) || incident.asset.includes(entity.n)
    ).map((entity) => entity.n);
    if (named.length) return named;
    // An asset such as "backup-vault" is no entity: it becomes an element of its own.
    if (!infraByName.has(incident.asset)) {
      const domain = domainOfAsset(incident.asset);
      infraByName.set(incident.asset, {
        _id: infraId(incident.asset),
        name: incident.asset,
        provider: domain,
        provider_type: domain,
        universal_type: domain,
        universal_domain: domain,
        external_id: incident.asset,
        ...assetTags(incident.asset),
        links: [],
      });
    }
    return [incident.asset];
  };

  // --- Threats, actions, remediations, candidates and the events behind them.
  const threatEvents: ThreatEventItem[] = [];
  const threatCandidates: ThreatEventCandidateItem[] = [];
  const actions: ThreatElementDocument[] = [];
  const remediations: RemediationItem[] = [];
  const securityEvents: SecurityEventItem[] = [];
  const actionIdOf = new Map<string, string>();

  incidents.forEach((incident, n) => {
    const number = incident.id.replace(/^INC-/, '');
    const formed = 20 + n * 11;
    const labels = sourceLabels(incident.source);
    const provider = providerSlug(labels[0] ?? incident.source);
    const candidateId = `CAND-${number}`;

    if (incident.state === 'suppressed') {
      threatCandidates.push({
        _id: candidateId,
        source: provider,
        source_id: `demo-${provider}`,
        analysis_status: 'completed',
        outcome: 'false_positive',
        status: 'false_positive',
        decision_reasoning: incident.detail.reasoning,
        detected_at: iso(formed + 2),
        processed_at: iso(formed),
      });
      return;
    }

    threatCandidates.push({
      _id: candidateId,
      source: provider,
      source_id: `demo-${provider}`,
      analysis_status: 'completed',
      outcome: 'threat',
      status: 'threat',
      detected_at: iso(formed + 12),
      processed_at: iso(formed),
    });

    const earlier = EARLIER_IN_LIFE[incident.id];
    const detectionStatus =
      incident.state === 'auto'
        ? 'remediated'
        : incident.state === 'progress' && !earlier
          ? 'in_progress'
          : incident.state === 'reverted'
            ? 'reverted'
            : 'active';
    threatEvents.push({
      _id: incident.id,
      candidate_id: candidateId,
      provider,
      detection_classification: incident.title,
      detection_description: incident.detail.reasoning,
      detection_risk_level: incident.sev,
      detection_status: detectionStatus,
      first_event_time: iso(formed + 12),
      created_at: iso(formed),
      formed_at: iso(formed),
    });

    const refs = infraRefsOf(incident);
    const actionId = `ACT-${number}`;
    actionIdOf.set(incident.id, actionId);
    actions.push({
      _id: actionId,
      element_type: 'action',
      name: incident.title,
      description: incident.detail.reasoning,
      threat_event_id: incident.id,
      status: incident.state === 'auto' ? 'remediated' : 'active',
      detection: { provider, detection_risk_level: incident.sev },
      infrastructure_refs: refs.map((name) => link(infraId(name), 'TARGETS')),
      links: refs.map((name) => link(infraId(name), 'TARGETS')),
    });

    // The plan: each state the feed shows has the plan that leads to it.
    const gated = incident.state === 'awaiting' || incident.state === 'reverted' || Boolean(incident.xdom);
    const plan: RemediationItem = {
      _id: `REM-${number}`,
      title: incident.detail.chain[0]?.[0] ?? incident.title,
      threat_event_id: incident.id,
      status: 'pending',
      execution_mode: gated ? 'gated' : 'auto',
      operations: incident.detail.chain.map(([step]) => ({ step })),
      formed_at: iso(formed),
      assigned_at: iso(formed - 1),
    };
    if (incident.state === 'progress') plan.status = 'in_progress';
    if (incident.state === 'auto' || incident.state === 'reverted') {
      plan.status = incident.state === 'auto' ? 'completed' : 'reverted';
      plan.decided_at = iso(formed - 3);
      plan.verified_at = iso(formed - 8);
    }
    if (incident.state === 'progress') plan.decided_at = iso(formed - 3);
    if (earlier === 'assigned' || earlier === 'unassigned') {
      // The analysis is still drawing up the plan; nobody has the second one yet.
      plan.status = 'pending';
      plan.operations = [];
      delete plan.decided_at;
      if (earlier === 'unassigned') delete plan.assigned_at;
    }
    if (earlier === 'escalated') plan.escalated_at = iso(formed - 2);
    if (incident.state === 'reverted') {
      plan.reverted_at = iso(formed - 10);
      plan.revert_cause = 'human_rollback';
    }
    remediations.push(plan);

    // One raw event per signal the incident joined.
    incident.detail.signals.forEach(([, tool, text], k) => {
      securityEvents.push({
        _id: `EVT-${number}-${k + 1}`,
        threat_event_id: incident.id,
        candidate_id: candidateId,
        provider: providerSlug(tool),
        product: tool,
        event_id: `evt-${number}-${k + 1}`,
        event_time: iso(formed + 12 - k * 2),
        ingestion_time: iso(formed + 11 - k * 2),
        event_type: text,
        action: text,
        actor_name: refs[0],
        actor_type: domainOfAsset(refs[0]),
        success: 'True',
        is_duplicate: 'False',
      });
    });
  });

  // The rest of what the pipeline saw in the same hours: verdicts that did not become incidents.
  const quiet: [string, string, string][] = [
    ['not_threat', 'okta', 'Routine admin console login'],
    ['not_threat', 'aws_cloudtrail', 'Scheduled snapshot'],
    ['not_threat', 'crowdstrike_falcon', 'Signed driver update'],
    ['not_threat', 'okta', 'Password change by the owner'],
    ['not_threat', 'prisma_cloud', 'Tag change on a bucket'],
    ['duplicated', 'aws_cloudtrail', 'Repeat of a processed batch'],
    ['duplicated', 'okta', 'Repeat of a processed batch'],
  ];
  quiet.forEach(([outcome, source, note], k) => {
    threatCandidates.push({
      _id: `CAND-Q${k + 1}`,
      source,
      source_id: `demo-${source}`,
      analysis_status: 'completed',
      outcome,
      status: outcome,
      decision_reasoning: note,
      detected_at: iso(30 + k * 17),
      processed_at: iso(28 + k * 17),
    });
  });
  threatCandidates.push({
    _id: 'CAND-Q8',
    source: 'okta',
    source_id: 'demo-okta',
    analysis_status: 'analyzing',
    status: 'analyzing',
    detected_at: iso(2),
  });

  // --- Campaigns: only the members that exist as incidents here.
  const campaigns: ThreatElementDocument[] = C3_CAMPAIGNS.map((campaign) => ({
    _id: campaign.id,
    element_type: 'campaign',
    name: campaign.name,
    description: campaign.summary,
    status: 'active',
    links: campaign.members
      .filter((id) => actionIdOf.has(id) && CAMP_OF[id] === campaign.id)
      .map((id) => link(actionIdOf.get(id) as string, 'CONTAINS')),
  }));

  // --- Entities: how risky they are and how far an attacker gets from them.
  const referenced = new Set(
    actions.flatMap((action) => (action.infrastructure_refs ?? []) as { value: string }[]).map((ref) => ref.value)
  );
  const infrastructureElements = Array.from(infraByName.values());
  // Only elements something refers to, or an entity, are worth a node in the graph.
  const keptInfra = infrastructureElements.filter(
    (el) => entityNames.includes(String(el.name)) || referenced.has(String(el._id))
  );

  const businessElements: BusinessElementDocument[] = BUSINESS_SERVICES.map((service) => ({
    _id: service.id,
    name: service.name,
    element_type: service.type,
    description: service.description,
    ...assetTags(service.name),
    links: [],
    infrastructure_refs: service.runsOn
      .filter((name) => infraByName.has(name))
      .map((name) => link(infraId(name), 'RUNS_ON')),
  }));

  const entityRiskScores: EntityRiskScoreDocument[] = C3_ENTITIES.map((entity) => {
    const delta = trendValue(entity.trend);
    const related = entity.rel.filter((id) => actionIdOf.has(id));
    return {
      _id: `ERI-${providerSlug(entity.n)}`,
      element_id: infraId(entity.n),
      element_name: entity.n,
      score: entity.score,
      band: bandOf(entity.score),
      previous_score: entity.score - delta,
      score_delta_7d: delta,
      computed_at: iso(30),
      top_drivers: related.map((id) => ({
        label: `Linked to ${id}`,
        contribution: Math.round(entity.score / Math.max(1, related.length)),
      })),
      attacking_actions: related.map((id) => ({
        action_id: actionIdOf.get(id),
        name: incidents.find((incident) => incident.id === id)?.title,
        success: true,
      })),
      attacking_campaigns: Array.from(new Set(related.map((id) => CAMP_OF[id]).filter(Boolean))),
    };
  });

  const blastRadiusScores: BlastRadiusScoreDocument[] = C3_ENTITIES.map((entity) => {
    const score = Math.round(entity.comps.reduce((sum, c) => sum + c, 0) / entity.comps.length);
    const reached = BUSINESS_SERVICES.filter((service) => service.runsOn.includes(entity.n));
    return {
      _id: `EBR-${providerSlug(entity.n)}`,
      element_id: infraId(entity.n),
      element_name: entity.n,
      ebr_score: score,
      score,
      band: bandOf(score),
      reachability_count: entity.rel.length * 4 + reached.length * 3 + 2,
      max_domain: domainOfEntity(entity).domain,
      universal_domain: domainOfEntity(entity).domain,
      computed_at: iso(30),
      reachable_business_elements: {
        count: reached.length,
        items: reached.map((service) => ({
          id: service.id,
          name: service.name,
          element_type: service.type,
          criticality: 8,
        })),
      },
    };
  });

  const sources: SourceItem[] = DEMO_SOURCE_CATALOG.filter((item) => item.connected).map((item, k) => ({
    _id: item.id,
    name: item.name,
    provider: item.provider,
    status: 'active',
    dropzone_type: 's3',
    last_scanned_at: iso(1 + k),
    last_status: 'ok',
    candidate_source: providerSlug(item.name),
  }));

  return {
    threatEvents,
    threatCandidates,
    threatElements: [...actions, ...campaigns],
    infrastructureElements: keptInfra,
    businessElements,
    securityEvents,
    remediations,
    blastRadiusScores,
    entityRiskScores,
    sources,
  };
}

export function createFixtureSource(options: FixtureOptions = {}): C3DataSource {
  const now = options.now ?? Date.now();
  const records = buildFixtureRecords({ now });
  // A copy of each list, so a screen that sorts in place cannot change what the next read gets.
  const read = <T>(list: T[]) => Promise.resolve([...list]);

  return {
    kind: 'fixture',
    listThreatEvents: () => read(records.threatEvents),
    listThreatCandidates: () => read(records.threatCandidates),
    listThreatElements: () => read(records.threatElements),
    listInfrastructureElements: () => read(records.infrastructureElements),
    listBusinessElements: () => read(records.businessElements),
    listSecurityEvents: () => read(records.securityEvents),
    listRemediations: () => read(records.remediations),
    listBlastRadiusScores: () => read(records.blastRadiusScores),
    listEntityRiskScores: () => read(records.entityRiskScores),
    listSources: () => read(records.sources),
    getFunnelCounters: (nowMs: number, window: FunnelWindow = DEFAULT_FUNNEL_WINDOW) =>
      Promise.resolve(
        computeFunnelCounters(
          {
            candidates: records.threatCandidates,
            events: records.securityEvents,
            threatEvents: records.threatEvents,
            elements: records.threatElements,
            remediations: records.remediations,
            sources: records.sources,
          },
          nowMs,
          window
        )
      ),
  };
}
